import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Card, Skeleton } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { lei } from '@/lib/money'
import { RapoarteBani } from './reports'

/* ──────────────── bucati comune ──────────────── */

const proc = (n: number) => `${n >= 0 ? '' : ''}${n.toFixed(1)}%`

function Kpi({ eticheta, valoare, sub, ton }: {
  eticheta: string; valoare: string; sub?: string; ton?: 'bun' | 'rau'
}) {
  return (
    <Card className="p-4">
      <p className="text-[12.5px] text-muted">{eticheta}</p>
      <p className={'mt-1 tnum text-[22px] leading-none ' +
        (ton === 'rau' ? 'text-bad' : ton === 'bun' ? 'text-good' : '')}>{valoare}</p>
      {sub && <p className="mt-1.5 text-[12.5px] text-muted">{sub}</p>}
    </Card>
  )
}

function Incarcare() {
  return <Card><Skeleton rows={6} /></Card>
}

/** Bara de proportie — cat din vanzare ramane marja. Se citeste dintr-o privire. */
function Bara({ parte, tot, culoare = 'bg-accent' }: { parte: number; tot: number; culoare?: string }) {
  const p = tot > 0 ? Math.max(0, Math.min(100, (parte / tot) * 100)) : 0
  return (
    <span className="inline-block w-24 h-1.5 rounded-full bg-page align-middle overflow-hidden">
      <span className={`block h-full ${culoare}`} style={{ width: `${p}%` }} />
    </span>
  )
}

/* ──────────────── 1. Vedere de ansamblu ──────────────── */

type Sumar = {
  period: Record<string, string>
  sales: { total: number; growth: number; previous: number }
  costs?: { total: number }
  profit: { total: number; margin: number }
  reservations?: { total: number; new: number }
  cash: { collected: number; overdue: number; collection_rate: number }
}
type PeLuna = {
  year: number
  months: { month: number; month_name: string; total_sales: number; total_collected: number
            difference: number; invoice_count: number }[]
  totals: { total_sales: number; total_collected: number; difference: number }
}

export function RapoarteAnsamblu() {
  const s = useQuery({ queryKey: ['rap-sumar'], queryFn: () => api.get<Sumar>('/reports/executive-summary') })
  const l = useQuery({ queryKey: ['rap-luni'], queryFn: () => api.get<PeLuna>('/reports/by-month') })

  const coloane: Coloana<PeLuna['months'][number]>[] = [
    { cheie: 'luna', antet: 'Luna', sortabil: true, valoare: (m) => m.month,
      randare: (m) => <span className="font-medium">{m.month_name}</span> },
    { cheie: 'facturi', antet: 'Facturi', sortabil: true, aliniat: 'dreapta', valoare: (m) => m.invoice_count,
      randare: (m) => <span className="tnum text-muted">{m.invoice_count}</span> },
    { cheie: 'vanzari', antet: 'Facturat', sortabil: true, aliniat: 'dreapta', valoare: (m) => m.total_sales,
      randare: (m) => <span className="tnum">{lei(m.total_sales)}</span> },
    { cheie: 'incasat', antet: 'Incasat', sortabil: true, aliniat: 'dreapta', valoare: (m) => m.total_collected,
      randare: (m) => <span className="tnum text-good">{lei(m.total_collected)}</span> },
    { cheie: 'rest', antet: 'Ramas', sortabil: true, aliniat: 'dreapta', valoare: (m) => m.difference,
      randare: (m) => (m.difference > 0
        ? <span className="tnum text-bad">{lei(m.difference)}</span>
        : <span className="tnum text-muted">{lei(m.difference)}</span>) },
  ]

  return (
    <div className="px-8 py-6 space-y-6">
      {s.isPending ? <Incarcare /> : s.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi eticheta="Vanzari (perioada)" valoare={`${lei(s.data.sales.total)} lei`}
            sub={`${proc(s.data.sales.growth)} fata de perioada anterioara`}
            ton={s.data.sales.growth >= 0 ? 'bun' : 'rau'} />
          <Kpi eticheta="Profit" valoare={`${lei(s.data.profit.total)} lei`}
            sub={`marja ${proc(s.data.profit.margin)}`}
            ton={s.data.profit.total >= 0 ? 'bun' : 'rau'} />
          <Kpi eticheta="Incasat" valoare={`${lei(s.data.cash.collected)} lei`}
            sub={`rata de incasare ${proc(s.data.cash.collection_rate)}`} />
          <Kpi eticheta="Restant" valoare={`${lei(s.data.cash.overdue)} lei`}
            ton={s.data.cash.overdue > 0 ? 'rau' : undefined} />
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h3 className="text-[15px] font-semibold">Pe luni {l.data ? `· ${l.data.year}` : ''}</h3>
          {l.data && (
            <span className="text-[13px] text-muted tnum">
              facturat {lei(l.data.totals.total_sales)} lei · incasat {lei(l.data.totals.total_collected)} lei
            </span>
          )}
        </div>
        <DataTable randuri={l.data?.months ?? []} coloane={coloane} cheie={(m) => m.month}
          incarca={l.isPending} sortImplicit={{ cheie: 'luna', sens: 'asc' }}
          gol={{ titlu: 'Fara date pe anul curent' }} />
      </section>
    </div>
  )
}

/* ──────────────── 2. Vanzari si marja ──────────────── */

type Profit = {
  summary: { total_sales: number; total_costs: number; total_margin: number; avg_margin_percent: number }
  items: { group: string; invoices: number; sales: number; costs: number; margin: number; margin_percent: number }[]
}
type Agenti = {
  agents: { agent: string; total_sales: number; total_collected: number; difference: number
            invoice_count: number; collection_rate: number }[]
  totals: { total_sales: number; total_collected: number; collection_rate: number }
}

export function RapoarteVanzari() {
  const p = useQuery({ queryKey: ['rap-profit'], queryFn: () => api.get<Profit>('/reports/profitability') })
  const a = useQuery({ queryKey: ['rap-agenti'], queryFn: () => api.get<Agenti>('/reports/by-agent') })

  const colProfit: Coloana<Profit['items'][number]>[] = [
    { cheie: 'grup', antet: 'Perioada', sortabil: true, valoare: (x) => x.group,
      randare: (x) => <span className="font-medium tnum">{x.group}</span> },
    { cheie: 'facturi', antet: 'Facturi', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.invoices,
      randare: (x) => <span className="tnum text-muted">{x.invoices}</span> },
    { cheie: 'vanzari', antet: 'Vanzari', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.sales,
      randare: (x) => <span className="tnum">{lei(x.sales)}</span> },
    { cheie: 'costuri', antet: 'Costuri', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.costs,
      randare: (x) => <span className="tnum text-muted">{lei(x.costs)}</span> },
    { cheie: 'marja', antet: 'Marja', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.margin,
      randare: (x) => (
        <span className="inline-flex items-center gap-2">
          <Bara parte={Math.max(0, x.margin)} tot={x.sales} culoare={x.margin >= 0 ? 'bg-good' : 'bg-bad'} />
          <span className={'tnum ' + (x.margin < 0 ? 'text-bad' : '')}>{lei(x.margin)}</span>
        </span>
      ) },
    { cheie: 'procent', antet: '%', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.margin_percent,
      randare: (x) => <span className={'tnum ' + (x.margin_percent < 0 ? 'text-bad' : 'text-good')}>
        {proc(x.margin_percent)}</span> },
  ]

  const colAgent: Coloana<Agenti['agents'][number]>[] = [
    { cheie: 'agent', antet: 'Agent', sortabil: true, valoare: (x) => x.agent,
      randare: (x) => <span className="font-medium">{x.agent}</span> },
    { cheie: 'facturi', antet: 'Facturi', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.invoice_count,
      randare: (x) => <span className="tnum text-muted">{x.invoice_count}</span> },
    { cheie: 'vanzari', antet: 'Vanzari', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.total_sales,
      randare: (x) => <span className="tnum">{lei(x.total_sales)}</span> },
    { cheie: 'incasat', antet: 'Incasat', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.total_collected,
      randare: (x) => <span className="tnum text-good">{lei(x.total_collected)}</span> },
    { cheie: 'rata', antet: 'Rata de incasare', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.collection_rate,
      randare: (x) => (
        <span className="inline-flex items-center gap-2">
          <Bara parte={x.collection_rate} tot={100} />
          <span className="tnum">{proc(x.collection_rate)}</span>
        </span>
      ) },
  ]

  return (
    <div className="px-8 py-6 space-y-6">
      {p.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi eticheta="Vanzari" valoare={`${lei(p.data.summary.total_sales)} lei`} />
          <Kpi eticheta="Costuri" valoare={`${lei(p.data.summary.total_costs)} lei`} />
          <Kpi eticheta="Marja" valoare={`${lei(p.data.summary.total_margin)} lei`}
            ton={p.data.summary.total_margin >= 0 ? 'bun' : 'rau'} />
          <Kpi eticheta="Marja medie" valoare={proc(p.data.summary.avg_margin_percent)} />
        </div>
      )}

      <section className="space-y-3">
        <h3 className="text-[15px] font-semibold">Marja pe luni</h3>
        <DataTable randuri={p.data?.items ?? []} coloane={colProfit} cheie={(x) => x.group}
          incarca={p.isPending} sortImplicit={{ cheie: 'grup', sens: 'desc' }}
          gol={{ titlu: 'Fara date' }} />
      </section>

      <section className="space-y-3">
        <h3 className="text-[15px] font-semibold">Pe agent</h3>
        <DataTable randuri={a.data?.agents ?? []} coloane={colAgent} cheie={(x) => x.agent}
          incarca={a.isPending} sortImplicit={{ cheie: 'vanzari', sens: 'desc' }}
          gol={{ titlu: 'Fara date' }} />
      </section>
    </div>
  )
}

/* ──────────────── 3. Clienti si destinatii ──────────────── */

type Clienti = { clients: { client: string; total_sales: number; total_collected: number
                            difference: number; invoice_count: number }[] }
type Destinatii = { destinations: { destination: string; bookings: number; unique_customers: number
                                    revenue: number; cost: number; profit: number; margin: number
                                    avg_booking_value: number }[] }

export function RapoarteClienti() {
  const c = useQuery({ queryKey: ['rap-clienti'], queryFn: () => api.get<Clienti>('/reports/top-clients') })
  const d = useQuery({ queryKey: ['rap-destinatii'], queryFn: () => api.get<Destinatii>('/reports/top-destinations') })

  const colC: Coloana<Clienti['clients'][number]>[] = [
    { cheie: 'client', antet: 'Client', sortabil: true, valoare: (x) => x.client,
      randare: (x) => <span className="font-medium">{x.client}</span> },
    { cheie: 'facturi', antet: 'Facturi', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.invoice_count,
      randare: (x) => <span className="tnum text-muted">{x.invoice_count}</span> },
    { cheie: 'vanzari', antet: 'Facturat', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.total_sales,
      randare: (x) => <span className="tnum">{lei(x.total_sales)}</span> },
    { cheie: 'incasat', antet: 'Incasat', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.total_collected,
      randare: (x) => <span className="tnum text-good">{lei(x.total_collected)}</span> },
    { cheie: 'rest', antet: 'Ramas', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.difference,
      randare: (x) => (x.difference > 25
        ? <span className="tnum text-bad">{lei(x.difference)}</span>
        : <span className="text-muted">—</span>) },
  ]

  const colD: Coloana<Destinatii['destinations'][number]>[] = [
    { cheie: 'dest', antet: 'Destinatie', sortabil: true, valoare: (x) => x.destination,
      randare: (x) => <span className="font-medium">{x.destination}</span> },
    { cheie: 'rez', antet: 'Rezervari', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.bookings,
      randare: (x) => <span className="tnum">{x.bookings}</span> },
    { cheie: 'clienti', antet: 'Clienti', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.unique_customers,
      randare: (x) => <span className="tnum text-muted">{x.unique_customers}</span> },
    { cheie: 'venit', antet: 'Incasari', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.revenue,
      randare: (x) => <span className="tnum">{lei(x.revenue)}</span> },
    { cheie: 'profit', antet: 'Profit', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.profit,
      randare: (x) => (
        <span className="inline-flex items-center gap-2">
          <Bara parte={Math.max(0, x.profit)} tot={x.revenue} culoare={x.profit >= 0 ? 'bg-good' : 'bg-bad'} />
          <span className="tnum">{lei(x.profit)}</span>
        </span>
      ) },
    { cheie: 'marja', antet: 'Marja', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.margin,
      randare: (x) => <span className="tnum text-muted">{proc(x.margin)}</span> },
    { cheie: 'medie', antet: 'Valoare medie', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.avg_booking_value,
      randare: (x) => <span className="tnum text-muted">{lei(x.avg_booking_value)}</span> },
  ]

  return (
    <div className="px-8 py-6 space-y-6">
      <section className="space-y-3">
        <h3 className="text-[15px] font-semibold">Cei mai buni clienti</h3>
        <DataTable randuri={c.data?.clients ?? []} coloane={colC} cheie={(x) => x.client}
          incarca={c.isPending} sortImplicit={{ cheie: 'vanzari', sens: 'desc' }}
          gol={{ titlu: 'Fara date' }} />
      </section>
      <section className="space-y-3">
        <h3 className="text-[15px] font-semibold">Destinatii</h3>
        <DataTable randuri={d.data?.destinations ?? []} coloane={colD} cheie={(x) => x.destination}
          incarca={d.isPending} sortImplicit={{ cheie: 'venit', sens: 'desc' }}
          gol={{ titlu: 'Fara date' }} />
      </section>
    </div>
  )
}

/* ──────────────── 4. Furnizori ──────────────── */

type Furnizori = {
  summary: { total_suppliers: number; total_purchases: number; total_paid: number
             total_unpaid: number; total_overdue: number; payment_rate: number }
  suppliers: { name: string; invoices: number; bookings: number; total_value: number
               total_paid: number; unpaid: number; overdue: number; payment_rate: number
               avg_invoice_value: number; is_critical: boolean }[]
}

export function RapoarteFurnizori() {
  const f = useQuery({ queryKey: ['rap-furnizori'], queryFn: () => api.get<Furnizori>('/reports/supplier-analysis') })

  const col: Coloana<Furnizori['suppliers'][number]>[] = [
    { cheie: 'nume', antet: 'Furnizor', sortabil: true, valoare: (x) => x.name,
      randare: (x) => (
        <span>
          <span className="font-medium">{x.name}</span>
          {x.is_critical && <Badge tone="warn">critic</Badge>}
        </span>
      ) },
    { cheie: 'facturi', antet: 'Facturi', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.invoices,
      randare: (x) => <span className="tnum text-muted">{x.invoices}</span> },
    { cheie: 'valoare', antet: 'Cumparat', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.total_value,
      randare: (x) => <span className="tnum">{lei(x.total_value)}</span> },
    { cheie: 'platit', antet: 'Platit', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.total_paid,
      randare: (x) => <span className="tnum text-good">{lei(x.total_paid)}</span> },
    { cheie: 'neplatit', antet: 'De platit', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.unpaid,
      randare: (x) => (x.unpaid > 25 ? <span className="tnum">{lei(x.unpaid)}</span> : <span className="text-muted">—</span>) },
    { cheie: 'restant', antet: 'Restant', sortabil: true, aliniat: 'dreapta', valoare: (x) => x.overdue,
      randare: (x) => (x.overdue > 25 ? <span className="tnum text-bad">{lei(x.overdue)}</span> : <span className="text-muted">—</span>) },
  ]

  return (
    <div className="px-8 py-6 space-y-6">
      {f.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi eticheta="Cumparat" valoare={`${lei(f.data.summary.total_purchases)} lei`}
            sub={`${f.data.summary.total_suppliers} furnizori`} />
          <Kpi eticheta="Platit" valoare={`${lei(f.data.summary.total_paid)} lei`} ton="bun" />
          <Kpi eticheta="De platit" valoare={`${lei(f.data.summary.total_unpaid)} lei`} />
          <Kpi eticheta="Restant" valoare={`${lei(f.data.summary.total_overdue)} lei`}
            ton={f.data.summary.total_overdue > 0 ? 'rau' : undefined} />
        </div>
      )}
      <DataTable randuri={f.data?.suppliers ?? []} coloane={col} cheie={(x) => x.name}
        incarca={f.isPending} sortImplicit={{ cheie: 'valoare', sens: 'desc' }}
        gol={{ titlu: 'Fara date' }} />
    </div>
  )
}

/* ──────────────── invelisul cu taburi ──────────────── */

export function Rapoarte({ copil }: { copil: 'ansamblu' | 'vanzari' | 'clienti' | 'furnizori' | 'bani' }) {
  const { pathname } = useLocation()
  const titlu = {
    ansamblu: 'Rapoarte', vanzari: 'Vanzari si marja', clienti: 'Clienti si destinatii',
    furnizori: 'Furnizori', bani: 'Bani, TVA si avansuri',
  }[copil]
  return (
    <>
      <PageHeader title={titlu} count={pathname === '/rapoarte' ? 'anul curent' : undefined} />
      {copil === 'ansamblu' && <RapoarteAnsamblu />}
      {copil === 'vanzari' && <RapoarteVanzari />}
      {copil === 'clienti' && <RapoarteClienti />}
      {copil === 'furnizori' && <RapoarteFurnizori />}
      {copil === 'bani' && <RapoarteBani />}
    </>
  )
}
